'use client';

/**
 * WeightStrip — compact body-weight readout with 7d/30d trend chips.
 *
 * Self-loads from DBDailyActivity (smart-scale readings within the last
 * ~45 days). Renders nothing when the user has no scale data. Down arrows
 * are emerald (the implicit lean/cut goal); up arrows are amber (neutral
 * caution, since "up" is bad for cutters and fine for bulkers — we don't
 * know which the user is, so we don't celebrate it).
 *
 * Used by the Lean and Hypertrophy persona dashboards. Both link out to
 * the Body comp tab on tap.
 */

import { useEffect, useState } from 'react';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

const KG_TO_LBS = 2.20462;

interface WeightPoint { t: number; lbs: number; }
interface WeightTrend {
  currentLbs: number;
  delta7: number | null;
  delta30: number | null;
}

/** Reading nearest to `daysAgo` before the latest point, within ±4 days. */
function nearest(points: WeightPoint[], latestT: number, daysAgo: number): number | null {
  const target = latestT - daysAgo * 86_400_000;
  const tol = 4 * 86_400_000;
  let best: WeightPoint | null = null;
  let bestDist = Infinity;
  for (const p of points) {
    const dist = Math.abs(p.t - target);
    if (dist < bestDist && dist <= tol) { best = p; bestDist = dist; }
  }
  return best ? best.lbs : null;
}

interface Props {
  userId?: string;
  /** Deep-link to the body composition tab on tap. */
  href?: string;
}

export default function WeightStrip({ userId, href }: Props) {
  const [trend, setTrend] = useState<WeightTrend | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      try {
        const { getDailyActivityEntries } = await import('@/lib/db');
        const since = new Date(Date.now() - 45 * 86_400_000);
        const entries = await getDailyActivityEntries(userId, { since });
        if (cancelled) return;
        const points: WeightPoint[] = entries
          .filter(e => typeof e.weight === 'number' && (e.weight as number) > 0)
          .map(e => ({ t: new Date(e.date).getTime(), lbs: (e.weight as number) * KG_TO_LBS }))
          .sort((a, b) => a.t - b.t);
        if (points.length === 0) return;
        const latest = points[points.length - 1];
        const w7 = nearest(points, latest.t, 7);
        const w30 = nearest(points, latest.t, 30);
        setTrend({
          currentLbs: latest.lbs,
          delta7: w7 != null ? latest.lbs - w7 : null,
          delta30: w30 != null ? latest.lbs - w30 : null,
        });
      } catch (e) {
        console.warn('[WeightStrip] load failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!trend) return null;

  function go() {
    // The metrics page restores its view/sub-tab from localStorage — set
    // both keys before navigating so we land directly on Analytics → Body.
    import('@/lib/storage').then(({ storage }) => {
      try {
        storage.set('metrics-active-view', 'analytics');
        storage.set('metrics-analytics-subtab', 'body');
      } catch { /* localStorage unavailable */ }
      window.location.href = href || '/metrics';
    });
  }

  return (
    <button
      onClick={go}
      className="w-full flex items-center gap-3 rounded-lg border border-operator-steel/40 bg-operator-slate/50 px-3.5 py-2.5 text-left hover:border-operator-steel transition"
    >
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">Weight</div>
        <div className="font-heading text-xl font-bold tabular-nums text-slate-100 leading-none mt-0.5">
          {trend.currentLbs.toFixed(1)}<span className="text-[11px] text-slate-500 font-normal ml-1">lb</span>
        </div>
      </div>
      <DeltaChip label="7 day" delta={trend.delta7} />
      <DeltaChip label="30 day" delta={trend.delta30} />
    </button>
  );
}

function DeltaChip({ label, delta }: { label: string; delta: number | null }) {
  if (delta == null) {
    return (
      <div className="flex flex-col items-end">
        <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-600">{label}</span>
        <span className="text-[11px] text-slate-600">—</span>
      </div>
    );
  }
  const rounded = Math.round(delta * 10) / 10;
  const flat = Math.abs(rounded) < 0.2;
  const down = rounded < 0;
  const tw = flat ? 'text-slate-400' : down ? 'text-emerald-400' : 'text-amber-400';
  const Icon = flat ? Minus : down ? TrendingDown : TrendingUp;
  return (
    <div className="flex flex-col items-end">
      <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-600">{label}</span>
      <span className={`flex items-center gap-1 text-[12px] font-semibold tabular-nums ${tw}`}>
        <Icon className="w-3 h-3" />
        {rounded > 0 ? '+' : ''}{rounded.toFixed(1)}
      </span>
    </div>
  );
}
